import { formatQuantity, receiptStatusLabel } from '../../utils/inventoryLabels';
import { EMPTY_RECEIPT_LINE, RECEIPT_STATUS } from './receiptsConstants';

export const formatReceiptMoment = (value) =>
  value
    ? new Date(value).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })
    : '-';

const sumOf = (lines, field) =>
  (lines || []).reduce((total, line) => total + Number(line[field] || 0), 0);

/**
 * How much an order expected and how much of it has arrived.
 *
 * The listing sends both totals; the detail sends only the lines, so they are
 * added up from there.
 *
 * @returns {{expected: number, received: number, missing: number}}
 */
export const unitsOf = (receipt) => {
  const expected = Number(receipt?.expected_units ?? sumOf(receipt?.lines, 'expected'));
  const received = Number(receipt?.received_units ?? sumOf(receipt?.lines, 'received'));
  return { expected, received, missing: Math.max(expected - received, 0) };
};

/**
 * Whether a product can arrive at the warehouse as itself.
 *
 * A virtual kit has no stock of its own: it is exploded into its components
 * when sold, and what reaches the dock are those components. The server
 * refuses it; not offering it keeps the refusal the exception.
 */
export const isReceivableProduct = (product) =>
  !(product.is_kit && product.kit_mode === 'virtual');

/** Pending or partial: something can still be received against it. */
export const isOpenReceipt = (receipt) =>
  receipt?.status === RECEIPT_STATUS.PENDING || receipt?.status === RECEIPT_STATUS.PARTIAL;

/* -------------------------------------------------------------------------- */
/*                                  Declaring                                 */
/* -------------------------------------------------------------------------- */

/**
 * The declaration form, seeded from the order being edited.
 *
 * @param {object|null} receipt - As the backend answers it; `null` for a new one.
 */
export const declareFormFrom = (receipt) =>
  receipt
    ? {
        supplier: receipt.supplier || '',
        reference: receipt.reference || '',
        notes: receipt.notes || '',
        lines: receipt.lines.map((line) => ({
          productId: line.product,
          expected: Number(line.expected),
          lot: line.lot || '',
          expiry_date: line.expiry_date || '',
        })),
      }
    : { supplier: '', reference: '', notes: '', lines: [{ ...EMPTY_RECEIPT_LINE }] };

/**
 * What is still missing before the order can be declared, as the sentence the
 * button shows while it is disabled — or `null` when it is ready to go.
 *
 * Lot and expiry are NOT asked for here even when the product controls them:
 * declaring is an expectation, and whoever declares often does not know them
 * yet. Counting is what demands them.
 */
export const declareProblem = (form, companyId) => {
  if (!companyId) return 'Elige el cliente';
  if (form.lines.length === 0) return 'Agrega al menos un SKU';
  if (form.lines.some((line) => !line.productId)) return 'Falta el producto de una línea';
  if (form.lines.some((line) => !(Number(line.expected) > 0))) {
    return 'Falta la cantidad de una línea';
  }
  return null;
};

const linesPayload = (lines) =>
  lines.map((line) => ({
    product_id: line.productId,
    expected: Number(line.expected),
    lot: line.lot.trim(),
    expiry_date: line.expiry_date || null,
  }));

/** The body of a new declaration. */
export const buildDeclarePayload = (form, { warehouseId, companyId }) => ({
  warehouse_id: warehouseId,
  company_id: companyId,
  supplier: form.supplier.trim(),
  reference: form.reference.trim(),
  notes: form.notes.trim(),
  lines: linesPayload(form.lines),
});

/** The body of an edit: the warehouse and the owner of an order do not change. */
export const buildDeclareUpdatePayload = (form) => ({
  supplier: form.supplier.trim(),
  reference: form.reference.trim(),
  notes: form.notes.trim(),
  lines: linesPayload(form.lines),
});

/* -------------------------------------------------------------------------- */
/*                                  Receiving                                 */
/* -------------------------------------------------------------------------- */

/** What is still to arrive of a line. Never negative: more can arrive than declared. */
export const pendingOf = (line) => Math.max(Number(line.expected) - Number(line.received), 0);

/**
 * The count form of an order: each line opens on what is still pending, with
 * the lot and the expiry that were declared, to be corrected against the box.
 *
 * @returns {{[lineId: number]: {quantity: number|string, lot: string, expiry_date: string}}}
 */
export const initialCounts = (receipt) =>
  Object.fromEntries(
    (receipt?.lines || []).map((line) => [
      line.id,
      { quantity: pendingOf(line), lot: line.lot || '', expiry_date: line.expiry_date || '' },
    ])
  );

/**
 * Whether the landing location can no longer be chosen.
 *
 * It is fixed by the first count: voiding the order reverts from one single
 * location, so the rest of the order lands where the first part did.
 */
export const isSlotFixed = (receipt) =>
  Boolean(receipt?.slot) && (receipt?.lines || []).some((line) => Number(line.received) > 0);

/** A line that already received with a lot keeps it: one line carries one lot. */
export const isLotFixed = (line) => Number(line.received) > 0 && Boolean(line.lot);

/** Which of the two a counted line is still missing, if any. */
export const missingOnLine = (line, count) => {
  if (!(Number(count?.quantity) > 0)) return { lot: false, expiry: false };
  return {
    lot: Boolean(line.lot_controlled) && !count.lot,
    expiry: Boolean(line.expiry_controlled) && !count.expiry_date,
  };
};

/**
 * What is still missing before the count can be confirmed, as the sentence the
 * button shows while it is disabled — or `null` when it is ready to go.
 *
 * What the product's flags demand is demanded here, before sending; the server
 * checks it again.
 */
export const receiveProblem = (receipt, counts) => {
  const lines = receipt?.lines || [];
  if (!lines.some((line) => Number(counts[line.id]?.quantity) > 0)) return 'Indica cuánto llegó';

  const missing = lines.map((line) => missingOnLine(line, counts[line.id]));
  const lot = missing.some((item) => item.lot);
  const expiry = missing.some((item) => item.expiry);
  if (lot && expiry) return 'Falta lote o vencimiento';
  if (lot) return 'Falta el lote';
  if (expiry) return 'Falta el vencimiento';

  return null;
};

/** The body of a count: only the lines something arrived of. */
export const buildReceivePayload = (receipt, counts, slotId) => ({
  counts: receipt.lines
    .filter((line) => Number(counts[line.id]?.quantity) > 0)
    .map((line) => ({
      line_id: line.id,
      quantity: Number(counts[line.id].quantity),
      lot: (counts[line.id].lot || '').trim(),
      expiry_date: counts[line.id].expiry_date || null,
    })),
  slot_id: slotId || null,
});

/* -------------------------------------------------------------------------- */
/*                     What a confirmation says, with numbers                 */
/* -------------------------------------------------------------------------- */

/** What closing with a shortage does, said with how much is missing. */
export const closeMessage = (receipt) => {
  const { expected, received, missing } = unitsOf(receipt);
  return `Se cerrará como recepcionada con un faltante de ${formatQuantity(missing)} un (${formatQuantity(
    received
  )} de ${formatQuantity(expected)} recibidas). El faltante queda registrado y no se podrá recibir más contra esta orden.`;
};

/** What voiding does: what is reverted, and what is kept. */
export const cancelMessage = (receipt) => {
  const { received } = unitsOf(receipt);
  const reverted =
    received > 0
      ? `Se revertirán las ${formatQuantity(
          received
        )} un recibidas, descontándolas de la ubicación de recepción. Solo es posible si nada de eso fue guardado todavía.`
      : 'Todavía no entró nada, así que no hay stock que revertir.';
  return `${reverted} La orden quedará como Anulada, con su historial: nada se borra.`;
};

/* -------------------------------------------------------------------------- */
/*                                    Excel                                   */
/* -------------------------------------------------------------------------- */

/**
 * The orders on screen as a sheet.
 *
 * @param {Array<object>} receipts - As `inventories/receipts/` sent them.
 */
export const buildReceiptsExportRows = (receipts) =>
  receipts.map((receipt) => {
    const { expected, received } = unitsOf(receipt);
    return {
      Orden: receipt.code,
      Cliente: receipt.company_name,
      Bodega: receipt.warehouse_name,
      Proveedor: receipt.supplier || '',
      Referencia: receipt.reference || '',
      Fecha: formatReceiptMoment(receipt.created_at),
      Ubicación: receipt.slot_name || '',
      Líneas: receipt.lines?.length || 0,
      Estado: receiptStatusLabel(receipt.status),
      Esperado: expected,
      Recibido: received,
    };
  });

/** @param {object|null} warehouse - The warehouse in use, or `null` for all. */
export const receiptsExportFileName = (warehouse) =>
  `recepciones-${warehouse ? warehouse.name : 'todas'}.xlsx`;
